import { generateTableData } from './tableData';
import { StyledLastRow, isPositiveSavings, mapCalculationToTableData } from './utils';
import {
  Box,
  Card,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { getBlueprintCalculations } from 'features/blueprints';
import { useTranslation } from 'react-i18next';
import { Blueprint } from 'types';

type BusinessCaseComparisonTableProps = {
  blueprintDetails: Blueprint;
};

export const BusinessCaseComparisonTable = ({ blueprintDetails }: BusinessCaseComparisonTableProps) => {
  const { t } = useTranslation();

  const { conventional: baseCalculation, hybrid: hybridCalculation } = getBlueprintCalculations(blueprintDetails);

  if (!baseCalculation || !hybridCalculation) {
    return null;
  }

  // Transform data to match tableData expectations
  const baseData = mapCalculationToTableData(baseCalculation, { batteryRentalCostCents: 0 });
  const hybridData = mapCalculationToTableData(hybridCalculation);

  const areCostsCalculatedForWholeProject = baseCalculation.calculationResult.calculationPeriod === 'project';

  const { costs: costsData, environmental: environmentalData } = generateTableData(
    t,
    baseData,
    hybridData,
    areCostsCalculatedForWholeProject
  );

  return (
    <Card sx={{ padding: 4, borderRadius: '8px' }}>
      <Typography variant="h6">{t('blueprints:details.table.title')}</Typography>

      <Grid container spacing={2}>
        {/* Costs Section */}
        <Grid item xs={12}>
          <Table>
            <TableHead>
              <TableRow sx={{ borderBottom: (theme) => `1px solid ${theme.palette.grey[200]}` }}>
                <TableCell sx={{ width: "30%" }}>
                  <Typography variant="caption" fontWeight="600">
                    {t('blueprints:details.table.costCategory')}
                  </Typography>
                </TableCell>
                {[
                  'blueprints:details.table.projectWithoutBattery',
                  'blueprints:details.table.greenerHybridSetup',
                  'blueprints:details.table.savings',
                ].map((key) => (
                  <TableCell key={key}>
                    <Typography variant="caption" color="grey.600">
                      {t(key)}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {costsData.map((row, index) => {
                const isLastRow = index === costsData.length - 1;
                const hasBottomBorder = index < costsData.length - 2;
                const RowComponent = isLastRow ? StyledLastRow : TableRow;

                return (
                  <RowComponent
                    key={index}
                    sx={{
                      borderBottom: hasBottomBorder ? (theme) => `1px solid ${theme.palette.grey[200]}` : 'none',
                    }}
                  >
                    <TableCell sx={{ border: 'none' }}>
                      <Typography variant="body2" color="grey.800">{row.label}</Typography>
                    </TableCell>

                    <TableCell sx={{ border: 'none' }}>
                      <Typography variant={isLastRow ? 'subtitle1' : 'body2'} fontWeight={isLastRow ? 600 : 400}>
                        {row.baseValue}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ border: 'none' }}>
                      <Typography variant={isLastRow ? 'subtitle1' : 'body2'} fontWeight={isLastRow ? 600 : 400}>
                        {row.hybridValue}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ border: 'none' }}>
                      <Typography
                        variant="subtitle2"
                        color={(theme) =>
                          isPositiveSavings(row.savings) ? theme.palette.success.main : theme.palette.grey[600]}
                      >
                        {isLastRow ? (
                          row.savings ? (
                            <Box
                              sx={{
                                padding: '8px 10px',
                                borderRadius: '4px',
                                background: (theme) =>
                                  isPositiveSavings(row.savings)
                                    ? theme.palette.success.background
                                    : theme.palette.grey[200],
                                lineHeight: '16px',
                                width: 'fit-content',
                              }}
                            >
                              {row.savings}
                            </Box>
                          ) : null
                        ) : (
                          row.savings || ''
                        )}
                      </Typography>
                    </TableCell>
                  </RowComponent>
                );
              })}
            </TableBody>
          </Table>
        </Grid>

        {/* Environmental Section */}
        <Grid item xs={12}>
          <Table>
            <TableHead>
              <TableRow sx={{ borderBottom: (theme) => `1px solid ${theme.palette.grey[200]}` }}>
                <TableCell sx={{ width: "30%" }}>
                  <Typography variant="caption" fontWeight="600">
                    {t('blueprints:details.table.environmentalCategory')}
                  </Typography>
                </TableCell>
                {[
                  'blueprints:details.table.projectWithoutBattery',
                  'blueprints:details.table.greenerHybridSetup',
                  'blueprints:details.table.savings',
                ].map((key) => (
                  <TableCell key={key}>
                    <Typography variant="caption" color="grey.600">
                      {t(key)}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {environmentalData.map((row, index) => {
                const hasBottomBorder = index < environmentalData.length - 1;

                return (
                  <TableRow
                    key={index}
                    sx={{
                      borderBottom: hasBottomBorder ? (theme) => `1px solid ${theme.palette.grey[200]}` : 'none',
                    }}
                  >
                    <TableCell sx={{ border: 'none' }}>
                      <Typography variant="body2" color="grey.800">{row.label}</Typography>
                    </TableCell>

                    <TableCell sx={{ border: 'none' }}>
                      <Typography variant="body2">
                        {row.baseValue}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ border: 'none' }}>
                      <Typography variant="body2">
                        {row.hybridValue}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ border: 'none' }}>
                      <Typography
                        variant="subtitle2"
                        color={(theme) =>
                          isPositiveSavings(row.savings) ? theme.palette.success.main : theme.palette.grey[600]}
                      >
                        {row.savings || ''}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Grid>
      </Grid>
    </Card>
  );
};
